import {
  Button,
  ButtonGroup,
  Input,
  ModalIcon,
  Spacer,
  Stack,
  TextBody,
  TextTitle,
} from '@pypestream/design-system';
import { FC, useEffect, useState } from 'react';

import { sendProStudioEvent } from '../../../xstate/app.xstate';
import { GenericModal, GenericModalProps } from '..';

type Env = 'live' | 'sandbox';

export type DeleteReportingApiCredentialModalProps = GenericModalProps & {
  credentialId?: string;
  pypeId?: string;
  credentialName?: string;
  env?: Env;
};

export const DeleteReportingApiCredentialModal: FC<
  DeleteReportingApiCredentialModalProps
> = ({
  credentialId,
  pypeId,
  credentialName = '',
  env = 'live',
  modalRef,
  onClose,
}) => {
  const [inputValue, setInputValue] = useState('');
  const [hasError, setHasError] = useState(false);

  useEffect(() => {
    setInputValue('');
    setHasError(false);
  }, [credentialId, credentialName]);

  const handleClose = () => {
    setInputValue('');
    setHasError(false);
    onClose?.();
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setHasError(false);
    setInputValue(e.target.value);
  };

  // The name is optional, and gating confirmation on `!!credentialName` left an
  // unnamed credential undeletable — no input could ever satisfy the check, so
  // the button stayed disabled forever. Fall back to a fixed, typeable word:
  // the confirmation step survives without depending on an optional field.
  const confirmationText = credentialName.trim() || 'delete';

  const handleDelete = () => {
    if (!credentialId || !pypeId) return;

    if (inputValue !== confirmationText) {
      setHasError(true);
      return;
    }

    sendProStudioEvent({
      type: 'deleteApiCredential',
      payload: { id: credentialId, pypeId, env, cb: handleClose },
    });
  };

  const isConfirmed = inputValue === confirmationText;

  return (
    <GenericModal
      test-id="delete-reporting-api-credential-modal"
      modalRef={modalRef}
      onClose={handleClose}
    >
      <Stack justifyContent="center">
        <ModalIcon name="error" />
      </Stack>
      <TextTitle size="small" textAlign="center">
        Delete this Reporting API Credential?
      </TextTitle>
      <Spacer size="small" />
      <TextBody variant="secondary" textAlign="center">
        Deleting this credential immediately revokes its key. Any integration
        using it will stop working.
      </TextBody>
      <Spacer size="large" />
      <TextBody variant="secondary">
        To verify, type{' '}
        <span color="warning">&ldquo;{confirmationText}&rdquo;</span> below
      </TextBody>
      <Spacer size="2xsmall" />
      <Input
        test-id="delete-reporting-api-credential-modal-input"
        variant="outlined"
        value={inputValue}
        onChange={handleInputChange}
        // @ts-expect-error - DS Input forwards onInput with target.value
        onInput={handleInputChange}
        hasError={hasError}
        helpText={hasError ? 'Please verify the credential name.' : ''}
        name="credential-name"
      />
      <ButtonGroup slot="footer">
        <Button
          test-id="delete-reporting-api-credential-modal-cancel"
          width="full"
          variant="secondary"
          size="large"
          onClick={handleClose}
        >
          Cancel
        </Button>
        <Button
          test-id="delete-reporting-api-credential-modal-delete"
          width="full"
          variant="warning"
          size="large"
          disabled={!isConfirmed}
          onClick={handleDelete}
        >
          Delete
        </Button>
      </ButtonGroup>
    </GenericModal>
  );
};
